import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "usehooks-ts";

import Search from "../../../components/select/Search";
import { getInitialsFromFullName } from "../../../constants/constants";
import { useIsPhone } from "../../../hooks/useIsPhone";
import { useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../../slices/ContactsSlice";
import { useGetSearchWorksQuery } from "../../../slices/WorkSlice";
import { SelectOptionType } from "../../../types/types";
import { AddContactModal } from "../../contacts/modals/addContact/AddContactModal";
import { navigateToContact } from "../../contacts/utils/navigateToContacts";
import {
  clientCaptions,
  sortClientsForSearch,
  uniqueClients,
} from "./searchAll.utils";

interface Props {
  /** "Ask ProCharted" row (only when AI is on). */
  aiAsk?: { onAsk: (question: string) => void };
}

// One search box for clients and work items (owner, 2026-10-21: no
// Contacts / Work items switch any more); results are listed under headings.
const SearchAll: React.FC<Props> = ({ aiAsk }) => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.appReducer);

  const [isFocus, setIsFocus] = useState(false);
  const isPhone = useIsPhone();
  const [isOpenAddContact, setIsOpenAddContact] = useState(false);
  // Text typed when "Create new contact" was clicked (pre-fills the names).
  const [newContactText, setNewContactText] = useState("");
  const [search, setSearch] = useState("");

  const debounceSearch = useDebounce(search, 300);
  const orgId = getLinkedFirm()?.orgId || "";

  const { data: contacts, isFetching: isLoadingContacts } =
    useGetSearchCustomersQuery({
      searchQuery: debounceSearch || "~~~",
      orgId,
      userId: user.id,
      isActive: true,
    });

  const { data: works, isFetching: isLoadingWorks } = useGetSearchWorksQuery(
    { query: debounceSearch, orgId },
    { skip: !debounceSearch.trim() },
  );

  const options: SelectOptionType[] = useMemo(() => {
    // Clients whose name matches come first (Roadmap 1802).
    const uniqueContacts = uniqueClients(contacts || []);
    const sortedContacts = debounceSearch.trim()
      ? sortClientsForSearch(
          uniqueContacts,
          debounceSearch,
          (contact) => contact.customerName,
        )
      : uniqueContacts;
    const captions = clientCaptions(sortedContacts);
    const contactOptions: SelectOptionType[] = sortedContacts.map(
      (contact, index) => {
        const avatarColor: "purple" | "blue" = contact.businessContactId
          ? "purple"
          : "blue";
        return {
          value: contact.businessContactId
            ? contact.businessContactId
            : contact.customerId,
          label: contact.customerName || "",
          avatar: getInitialsFromFullName(contact?.customerName),
          avatarColor,
          caption: captions[index],
          searchKind: "contact",
        };
      },
    );
    const workOptions: SelectOptionType[] = debounceSearch.trim()
      ? (works || []).map((work) => ({ ...work, searchKind: "work" }))
      : [];
    const heading = (label: string): SelectOptionType => ({
      value: `__heading_${label}__`,
      label,
      isGroupLabel: true,
      isDisabled: true,
    });
    return [
      ...(contactOptions.length ? [heading("Clients"), ...contactOptions] : []),
      ...(workOptions.length ? [heading("Work items"), ...workOptions] : []),
    ];
  }, [contacts, works, debounceSearch]);

  const openResult = (item: SelectOptionType) => {
    if (item.searchKind === "work") {
      navigate(`/work/${item.value}`);
      return;
    }
    navigate(
      navigateToContact({
        type: item.avatarColor === "purple" ? "organization" : "contact",
        customerId: item.value || "",
        id: item.value || "",
      }),
    );
  };

  return (
    <>
      <Search
        value={null}
        onChange={(item) => {
          const value = item as SelectOptionType;
          openResult(value);
        }}
        options={options}
        inputValue={search}
        onChangeInput={(value) => setSearch(value)}
        isLoading={isLoadingContacts || isLoadingWorks}
        placeholder={
          isFocus
            ? ""
            : aiAsk
            ? // Shorter on phones: the long text wrapped to 2 lines (QA batch 11).
              isPhone
              ? "Search or ask…"
              : "Search or ask a question…"
            : isPhone
            ? "Search…"
            : "Search clients and work"
        }
        aiAsk={aiAsk}
        noOptionsMessage={"No results"}
        onFocus={() => setIsFocus(true)}
        onBlur={() => setIsFocus(false)}
        setIsOpenAddContact={(open, typedText) => {
          setNewContactText(open ? (typedText || "").trim() : "");
          setIsOpenAddContact(open);
        }}
      />
      {isOpenAddContact && (
        <AddContactModal
          isIdLikeContactId={false}
          isOpen={isOpenAddContact}
          prefillText={newContactText}
          closeModal={() => setIsOpenAddContact(false)}
          setContactsForSort={(newContact) => {
            const newData: SelectOptionType = {
              label: newContact?.name || "",
              value: newContact?.isBusinessContact
                ? newContact?.id || ""
                : newContact?.customerId || "",
              avatarColor: newContact?.isBusinessContact ? "purple" : "blue",
            };
            setIsOpenAddContact(false);
            openResult(newData);
          }}
        />
      )}
    </>
  );
};

export default SearchAll;
